import React, { useEffect, useState } from 'react';

/**
 * Modal de confirmação para exclusão de ciclos de teste.
 * Mostra aviso explícito de perda permanente de acesso após exclusão.
 * Quando algum ciclo selecionado já está Concluído, exige uma confirmação
 * extra (checkbox) — apagar um ciclo concluído também apaga permanentemente
 * os resultados e evidências de execução registrados nele.
 */
const DeleteTestCycleModal = ({ isOpen, onClose, onConfirm, count = 0, cycles = [] }) => {
  const [acknowledgedCompleted, setAcknowledgedCompleted] = useState(false);

  useEffect(() => {
    if (isOpen) setAcknowledgedCompleted(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const completedCycles = cycles.filter((c) => String(c?.status || '').toLowerCase() === 'completed');
  const hasCompleted = completedCycles.length > 0;
  const canConfirm = !hasCompleted || acknowledgedCompleted;

  const renderSummary = () => {
    if (count <= 0) return null;
    if (count === 1) {
      const c = cycles[0] || {};
      const label = c?.name ? `${c.name}` : `Ciclo`;
      return (
        <p>
          Tem certeza que deseja excluir o ciclo <strong>{label}</strong>{c?.id ? ` (ID: ${c.id})` : ''}?
        </p>
      );
    }
    const preview = cycles.slice(0, 5);
    const remaining = Math.max(0, count - preview.length);
    return (
      <div>
        <p>Tem certeza que deseja excluir <strong>{count}</strong> ciclos selecionados?</p>
        {preview.length > 0 && (
          <ul style={{ marginTop: 8 }}>
            {preview.map((c) => (
              <li key={c.id}>
                {c?.name || 'Ciclo'}{c?.id ? ` (ID: ${c.id})` : ''}
              </li>
            ))}
            {remaining > 0 && (
              <li>… e mais {remaining}</li>
            )}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Excluir ciclo{count > 1 ? 's' : ''}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {renderSummary()}
          <div style={{
            marginTop: 12,
            padding: '12px 14px',
            border: '1px solid #fca5a5',
            background: '#fef2f2',
            color: '#7f1d1d',
            borderRadius: 6
          }}>
            <strong>Atenção:</strong> Esta ação é permanente e <strong>não pode ser desfeita</strong>.
            Após a exclusão, você <strong>não terá mais acesso</strong> ao(s) ciclo(s).
          </div>

          {hasCompleted && (
            <div style={{
              marginTop: 10,
              padding: '12px 14px',
              border: '1px solid #f59e0b',
              background: '#fffbeb',
              color: '#78350f',
              borderRadius: 6
            }}>
              <p style={{ margin: 0 }}>
                <strong>⚠️ {completedCycles.length > 1 ? 'Estes ciclos já estão' : 'Este ciclo já está'} Concluído{completedCycles.length > 1 ? 's' : ''}:</strong>
              </p>
              <ul style={{ margin: '6px 0' }}>
                {completedCycles.map((c) => (
                  <li key={c.id}>{c?.name || 'Ciclo'}</li>
                ))}
              </ul>
              <p style={{ margin: 0 }}>
                Excluir apaga junto, de forma permanente, as execuções vinculadas a ele e todos os resultados e evidências de execução já registrados.
              </p>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acknowledgedCompleted}
                  onChange={(e) => setAcknowledgedCompleted(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>Entendo que isso apaga permanentemente o histórico de execução concluído.</span>
              </label>
            </div>
          )}
        </div>
        <div className="modal-actions" style={{ padding: 20 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={!canConfirm}>Excluir definitivamente</button>
        </div>
      </div>
    </div>
  );
};

export default DeleteTestCycleModal;